import styled from "styled-components";
import { Label } from "../editor/EditorHelpers";
import { Button } from "./Button";
import { FiMoreHorizontal } from "react-icons/fi";
import { DefaultAndValue } from "../util/restore";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";


interface EditorHeaderWithPresetChooserProps {
  label?: string;
  value: string | DefaultAndValue;
  onChange: (v: string) => void;
}

export const EditorHeaderWithPresetChooser: React.FC<EditorHeaderWithPresetChooserProps> = ({
  label, value, onChange
}) => {
  const { t } = useTranslation()
  const [isMenuOpen, setMenuOpen] = useState(false);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  const isValueWithoutDefault = (typeof value === 'string');
  const hasPresets = !isValueWithoutDefault && (value.morePresets || (value.default !== value.value))
  const hasContent = label || hasPresets;
  const presets = hasPresets 
    ? [
      value.default,
      ...(value.morePresets || [])
    ].filter((v) => v !== value.value)
    : [];

  const handleSet = (v) => {
    onChange(v);
    setMenuOpen(false);
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && buttonRef.current
        && !dropdownRef.current.contains(event.target as Node)
        && !buttonRef.current.contains(event.target as Node)
      ) {
        setMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  const menuTextFromPresetValue = (v: string) => {
    const limit = 50;
    if (v.length <= limit) {
      return v;
    }
    return v.substring(0, 37) + "..." + v.substring(v.length - 10, v.length);
  };

  if (!hasContent) {
    return (<></>)
  }


  return (<Header>
    {label && <Label>{label}</Label>}
    <Filler />
    {hasPresets && <ButtonWrapper>
      <Button ref={buttonRef} $small $borderless onClick={() => setMenuOpen(!isMenuOpen)} style={{ transform: 'translateY(-1px)' }}>
        <FiMoreHorizontal size={14} style={{ verticalAlign: 'middle', display: 'inline-block'
      }} />
      </Button>
      <DropdownMenu ref={dropdownRef} $isOpen={isMenuOpen}>
        <Label style={{padding: '10px 10px 1px 10px'}}>{t('preset.label')}</Label>
        {presets.map((v) => (
          <li key={v} onClick={() => handleSet(v)}>
            {menuTextFromPresetValue(v)}
          </li>
        ))}
      </DropdownMenu>
    </ButtonWrapper>
    }
  </Header>)
}

const Header = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const Filler = styled.div`
  flex-grow: 1;
`;

const ButtonWrapper = styled.div`
  position: relative; /* This ensures the dropdown is positioned relative to the button */
  display: inline-block;
`;


const DropdownMenu = styled.ul<{ $isOpen: boolean }>`
  position: absolute;
  top: 80%; /* Position it directly below the button */
  right: 0; /* Align it to the right edge of the wrapper */
  margin: 10px 0 0;
  padding: 0;
  list-style: none;
  background-color: var(--bg-color);
  border: 1px solid var(--separator-color);
  border-radius: 8px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 100;
  display: ${(props) => (props.$isOpen ? 'block' : 'none')};

  li {
    display: flex; /* This ensures emoji and text are in the same row */
    align-items: center; /* Aligns the items vertically */
    white-space: nowrap; /* Prevent wrapping */
    padding: 10px 10px;
    cursor: pointer;

    &:hover {
      background-color: var(--control-bg-color);
      border-radius: 8px;
    }
  }
`;
